/* eslint-disable jsx-a11y/anchor-is-valid, no-script-url */

import React, { PureComponent } from 'react';
import { ComponentSettings } from '@bingads-webui-theme-2018/theme-fluent';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import type { ThemePalette } from '@bingads-webui-theme-2018/theme-fluent';
import type { HelpLinkType, LearnMoreLinkProps } from './types';

type LearnMoreLinkOuterProps = Omit<LearnMoreLinkProps, 'classes'>;

class UnStyledLearnMoreLink extends PureComponent<LearnMoreLinkProps> {
  static defaultProps: Partial<LearnMoreLinkProps> = {
    linkText: 'LearnMore',
    label: null,
    ariaLabel: null,
    text: '',
    href: '#!',
    linkType: 'helpPopup',
    onLinkClick: undefined,
    noLink: false,
  };

  render() {
    const {
      ariaLabel, label, text, linkText, topic, href, linkType, classes, onLinkClick, noLink,
    } = this.props;

    const helpTopic = `help:${topic}`;
    const aria = ariaLabel || `${label || text} ${linkText}`;
    const learnMoreLink: Record<HelpLinkType, React.ReactElement> = {
      helpPopup: (<a className="HelpTopic clickable" query={helpTopic} href={href} aria-label={aria} role="button">{linkText}</a>),
      fwdLink: (<a className="Fwlink clickable" href={`http://go.microsoft.com/fwlink?LinkId=${topic}`} target="_apexPlatformHelp" aria-label={aria} role="button">{linkText}</a>),
      delegate: (<a className="DelegateLink clickable" href={href} aria-label={aria} role="button" onClick={onLinkClick}>{linkText}</a>),
      directLink: (<a className="directLink clickable" href={href} aria-label={aria} target="_blank" rel="noreferrer">{linkText}</a>),
    };
    return (<span className={classes.link}>{text} {!noLink && learnMoreLink[linkType as HelpLinkType]}</span>);
  }
}
(UnStyledLearnMoreLink as typeof UnStyledLearnMoreLink & { propTypes: Record<string, unknown> }).propTypes = {
  text: PropTypes.string,
  topic: PropTypes.string.isRequired,
  linkText: PropTypes.string,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  href: PropTypes.string,
  linkType: PropTypes.string,
  classes: PropTypes.shape({
    link: PropTypes.string.isRequired,
  }).isRequired,
  onLinkClick: PropTypes.func,
  noLink: PropTypes.bool,
};

const { internalLinkStyle, underlineLinkStyle } = ComponentSettings;
const getUnderlineLinkStyle = (palette: ThemePalette, isGreyBackground: boolean) => underlineLinkStyle({ palette, elementLocator: 'a.HelpTopic, a.Fwlink, a.DelegateLink', isGreyBackground });
const getInternalLinkStyle = (palette: ThemePalette, isGreyBackground: boolean) => internalLinkStyle({ palette, elementLocator: 'a.HelpTopic, a.Fwlink, a.DelegateLink', isGreyBackground });
const GreyBackgroundUnderlineLink = withDefaultStyles(
  UnStyledLearnMoreLink,
  (_: unknown, { palette = {} }: { palette?: ThemePalette } = {}) => ({
    link: getUnderlineLinkStyle(palette, true),
  })
);
const NonGreyBackgroundUnderlineLink = withDefaultStyles(
  UnStyledLearnMoreLink,
  (_: unknown, { palette = {} }: { palette?: ThemePalette } = {}) => ({
    link: getUnderlineLinkStyle(palette, false),
  })
);
const GreyBackgroundInternalLink = withDefaultStyles(
  UnStyledLearnMoreLink,
  (_: unknown, { palette = {} }: { palette?: ThemePalette } = {}) => ({
    link: getInternalLinkStyle(palette, true),
  })
);
const NonGreyBackgroundInternalLink = withDefaultStyles(
  UnStyledLearnMoreLink,
  (_: unknown, { palette = {} }: { palette?: ThemePalette } = {}) => ({
    link: getInternalLinkStyle(palette, false),
  })
);

export const LearnMoreLink = ({
  isUnderlineLink,
  isGreyBackground,
  ...props
}: LearnMoreLinkOuterProps) => {
  let StyledLearnMoreLink: React.ComponentType<LearnMoreLinkOuterProps>;
  if (isUnderlineLink) {
    if (isGreyBackground) {
      StyledLearnMoreLink = GreyBackgroundUnderlineLink;
    } else {
      StyledLearnMoreLink = NonGreyBackgroundUnderlineLink;
    }
  } else if (isGreyBackground) {
    StyledLearnMoreLink = GreyBackgroundInternalLink;
  } else {
    StyledLearnMoreLink = NonGreyBackgroundInternalLink;
  }

  return <StyledLearnMoreLink {...props} />;
};

LearnMoreLink.defaultProps = {
  isUnderlineLink: true,
  isGreyBackground: false,
};

LearnMoreLink.propTypes = {
  isUnderlineLink: PropTypes.bool,
  isGreyBackground: PropTypes.bool,
};
